/**
 * This brand's finance config — served publicly by svc-setting.
 *
 * Resolved per brand: the gateway stamps `x-agent-id` from the host it already
 * bound to this player, and svc-setting merges that brand's `finance.config`
 * overrides over the platform row. Nothing here names an agent, and nothing here
 * can: a player only ever reads its own brand.
 *
 * Amounts are minor units; the wallet pages map them to bigint for the quick-pick
 * buttons. DEFAULTS mirror the backend so the UI renders before the fetch lands.
 */
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../../lib/api/client.js';
import { DEPOSIT_MODE_DEFAULTS, type DepositModeSwitches } from './deposit-modes.js';

export interface FinanceConfig extends DepositModeSwitches {
  depositEnabled: boolean;
  withdrawEnabled: boolean;
  depositQuickAmounts: number[];
  withdrawQuickAmounts: number[];
}

export const FINANCE_CONFIG_DEFAULTS: FinanceConfig = {
  depositEnabled: true,
  withdrawEnabled: true,
  depositQuickAmounts: [10000, 50000, 100000, 500000, 1000000],
  withdrawQuickAmounts: [50000, 100000, 500000, 1000000],
  ...DEPOSIT_MODE_DEFAULTS,
};

export function useFinanceConfig() {
  return useQuery({
    queryKey: ['finance', 'config'],
    queryFn: () => apiFetch<FinanceConfig>('/finance-config'),
    staleTime: 5 * 60_000,
    placeholderData: FINANCE_CONFIG_DEFAULTS,
  });
}
